import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import CryptoJS from "crypto-js";
import type { SelectedServer, UserServersResponse } from "@/helpers/interfaces/server";
import type { SelectedPanel } from "@/helpers/interfaces/panel";
import { fetchServerTierById, fetchUserServers } from "@/helpers/api/server";
import { clearAllCache } from "@/helpers/apiCache";
import { AdminLevel } from "@/helpers/types/adminLevel";

// --- TYPES & INTERFACES ---
const serverTierCache = ref<Record<string, { isPremium: boolean; fetchedAt: number }>>({});
const TIER_CACHE_MS = 5 * 60 * 1000;

export { AdminLevel };

export interface User {
  id: string;
  username: string;
  avatar: string | null;
  adminLevel: AdminLevel | null;
}

export interface SelectedApplication {
  id: string;
  name: string;
  serverId: string;
}

// Extend SelectedServer to include ownership tracking
interface OwnedSelectedServer extends SelectedServer {
  ownerId?: string;
}

export type UserServersPayload = UserServersResponse;

// Secret key for state encryption
const secretKey = import.meta.env.VITE_PINIA_SECRET_KEY;

export const useContextStore = defineStore(
  "context",
  () => {
    // --- STATE ---
    const currentServer = ref<OwnedSelectedServer | null>(null);
    const currentPanel = ref<SelectedPanel | null>(null);
    const user = ref<User | null>(null);
    const currentApplication = ref<SelectedApplication | null>(null);
    const isHydrated = ref<boolean>(false);

    // --- FRONTEND CACHE STATE ---
    const userServers = ref<UserServersPayload | null>(null);
    const userServersLastFetched = ref<number>(0);
    const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

    // --- GETTERS ---
    const serverId = computed(() => currentServer.value?.id ?? null);
    const hasServerSelected = computed(() => !!currentServer.value);
    const panelId = computed(() => currentPanel.value?.id ?? null);
    const hasPanelSelected = computed(() => !!currentPanel.value);
    const isLoggedIn = computed(() => !!user.value);
    const applicationId = computed(() => currentApplication.value?.id ?? null);
    const hasApplicationSelected = computed(() => !!currentApplication.value);

    // --- ACTIONS ---

    function clearAllSelections() {
      currentServer.value = null;
      currentPanel.value = null;
      currentApplication.value = null;
    }

    function setUser(userInfo: User | null) {
      user.value = userInfo;
    }

    async function getOrFetchServerTier(guildId: string, forceRefresh = false): Promise<boolean> {
      const cached = serverTierCache.value[guildId];
      const now = Date.now();

      if (!forceRefresh && cached && (now - cached.fetchedAt < TIER_CACHE_MS)) {
        // Also keep tierFetchStatus in sync if it got reset
        if (currentServer.value?.id === guildId && currentServer.value.tierFetchStatus !== 'success') {
          setServerTierStatus(guildId, 'success', cached.isPremium);
        }
        return cached.isPremium;
      }

      const isPremium = await fetchServerTierById(guildId);
      serverTierCache.value[guildId] = { isPremium, fetchedAt: now };
      setServerTierStatus(guildId, 'success', isPremium);
      return isPremium;
    }

    // Also clear it on logout in clearUser()
    function clearUser() {
      user.value = null;
      userServers.value = null;
      userServersLastFetched.value = 0;
      serverTierCache.value = {}; // <-- add this
      clearAllSelections();
      clearAllCache();
    }

    // --- FRONTEND CACHE ACTION ---
    async function getOrFetchUserServers(forceRefresh = false): Promise<UserServersPayload> {
      const now = Date.now();
      const isCacheValid = userServers.value && (now - userServersLastFetched.value < CACHE_DURATION_MS);

      // Return instant memory cache if valid and not forcing a refresh
      if (!forceRefresh && isCacheValid) {
        return userServers.value as UserServersPayload;
      }

      try {
        // Assuming fetchUserServers takes a boolean or config to append `?force=true`
        const data = await fetchUserServers(forceRefresh);

        // Update Cache
        userServers.value = data;
        userServersLastFetched.value = now;

        return data;
      } catch (error) {
        console.error("[ContextStore] Failed to fetch user servers:", error);
        throw error; // Let the router or component handle the error
      }
    }

    function selectServer(
      serverInfo:
        | (Partial<SelectedServer> & {
          id: string;
          name: string;
          avatarUrl?: string;
        })
        | null
    ) {
      if (serverInfo) {
        // 1. Check if we are actually changing the server ID
        const isDifferentServer = serverInfo.id !== currentServer.value?.id;

        // 2. Construct the new state, stamping it with the current User ID
        const newServerState: OwnedSelectedServer = {
          id: serverInfo.id,
          name: serverInfo.name,
          avatarUrl: serverInfo.avatarUrl,
          ownerId: user.value?.id, // <--- OWNER STAMPING

          // Preserve premium/tier status if staying on the same server, otherwise reset
          isPremium:
            serverInfo.isPremium !== undefined
              ? serverInfo.isPremium
              : isDifferentServer
                ? null
                : currentServer.value?.isPremium ?? null,

          tierFetchStatus:
            serverInfo.tierFetchStatus !== undefined
              ? serverInfo.tierFetchStatus
              : isDifferentServer
                ? "idle"
                : currentServer.value?.tierFetchStatus ?? "idle",
        };

        // 3. If the server ID changed, the selected panel is no longer valid
        if (isDifferentServer) {
          currentPanel.value = null;
        }

        currentServer.value = newServerState;
      } else {
        // Deselecting server
        clearAllSelections();
      }
    }

    function selectPanel(panel: SelectedPanel | null) {
      if (panel && !currentServer.value) {
        console.error(
          "[ContextStore] Cannot select a panel without a server selected first."
        );
        return;
      }

      // Only update if the panel ID is actually different
      if (
        panel?.id !== currentPanel.value?.id ||
        (panel && !currentPanel.value) ||
        (!panel && currentPanel.value)
      ) {
        currentPanel.value = panel;
      }
    }

    function updateSelectedPanelName(newName: string) {
      if (currentPanel.value) {
        currentPanel.value.name = newName;
      }
    }

    function updateSelectedPanelId(newPanelId: string) {
      if (currentPanel.value) {
        currentPanel.value.id = newPanelId;
      } else {
        console.warn(
          "[ContextStore] Attempted to update panel ID, but no panel is selected."
        );
      }
    }

    function selectApplication(app: SelectedApplication | null) {
      if (app && !currentServer.value) return;
      if (app?.id !== currentApplication.value?.id || (!app && currentApplication.value) || (app && !currentApplication.value)) {
        currentApplication.value = app;
      }
    }

    function updateSelectedApplicationName(newName: string) {
      if (currentApplication.value) currentApplication.value.name = newName;
    }

    function clearApplicationSelection() {
      currentApplication.value = null;
    }

    function clearServerSelection() {
      clearAllSelections();
    }

    function clearPanelSelection() {
      currentPanel.value = null;
    }

    function setServerTierStatus(
      serverIdForUpdate: string,
      status: "loading" | "success" | "error",
      isPremiumValue?: boolean | null
    ) {
      if (currentServer.value && currentServer.value.id === serverIdForUpdate) {
        currentServer.value.tierFetchStatus = status;
        if (status === "success") {
          currentServer.value.isPremium =
            typeof isPremiumValue === "boolean" ? isPremiumValue : null;
        } else {
          currentServer.value.isPremium = null;
        }
      }
    }

    // --- SECURITY WATCHER ---
    // Reactively ensure that the selected server belongs to the current user.
    // This catches hydration events, login swaps, and logout events.
    watch(
      () => user.value,
      (newUser) => {
        // Case 1: User logged out -> Clear everything
        if (!newUser) {
          userServers.value = null; // Clear cache on logout
          userServersLastFetched.value = 0;
          if (currentServer.value) clearAllSelections();
          return;
        }

        // Case 2: User logged in (or hydrated), but the server stored in state
        // belongs to a previous user (mismatched IDs).
        if (
          currentServer.value?.ownerId &&
          currentServer.value.ownerId !== newUser.id
        ) {
          console.warn(
            "[ContextStore] Detected server selection from a different user session. Clearing state."
          );
          clearAllSelections();
        }

        // Case 3: We have a user, and a server, but the server has NO ownerId (legacy state).
        // We implicitly adopt it to the current user to prevent annoying resets after this code deploy,
        // OR we clear it. Clearing it is safer.
        if (currentServer.value && !currentServer.value.ownerId) {
          clearAllSelections();
        }
      },
      { immediate: true }
    );

    return {
      // State
      user,
      currentServer,
      currentPanel,
      userServers, // Exported to be read in UI

      // Getters
      isLoggedIn,
      isHydrated,  // ✅ Export hydration flag for router guard usage
      serverId,
      hasServerSelected,
      panelId,
      hasPanelSelected,

      // Actions
      setUser,
      clearUser,
      getOrFetchUserServers, // Exported cache method
      getOrFetchServerTier,
      selectServer,
      selectPanel,
      updateSelectedPanelName,
      updateSelectedPanelId,
      clearServerSelection,
      clearPanelSelection,
      currentApplication,
      applicationId,
      hasApplicationSelected,
      selectApplication,
      updateSelectedApplicationName,
      clearApplicationSelection,
      clearAllSelections,
      setServerTierStatus,
    };
  },
  {
    persist: {
      // Notice we are NOT persisting `userServers`. It is better to start with an empty cache 
      // on a hard refresh so the user gets fresh data at least once per session.
      paths: ["user", "currentServer"],

      afterHydrate: (ctx) => {
        const store = ctx.store;
        if (store.currentServer && store.user) {
          if (store.currentServer.ownerId !== store.user.id) {
            console.warn("[ContextStore] Hydration mismatch detected. Resetting context.");
            store.currentServer = null;
            store.currentPanel = null;
          }
        } else if (store.currentServer && !store.user) {
          store.currentServer = null;
          store.currentPanel = null;
        }
        // ✅ ARCHITECTURE: Signal that hydration is complete for router guards
        store.isHydrated = true;
      },

      serializer: {
        serialize: (state: unknown) => {
          try {
            const s = state as {
              user?: User | null;
              currentServer?: OwnedSelectedServer | null;
            };
            // Persist only fields needed to restore identity & selection (smaller payload).
            const slim = {
              user: s.user
                ? {
                  id: s.user.id,
                  username: s.user.username,
                  adminLevel: s.user.adminLevel ?? null,
                  avatar: s.user.avatar,
                }
                : null,
              currentServer: s.currentServer
                ? {
                  id: s.currentServer.id,
                  name: s.currentServer.name,
                  ownerId: s.currentServer.ownerId,
                  avatarUrl: s.currentServer.avatarUrl ?? null,
                }
                : null,
            };
            return CryptoJS.AES.encrypt(JSON.stringify(slim), secretKey).toString();
          } catch (e) {
            console.error("Could not serialize and encrypt state:", e);
            return "";
          }
        },
        deserialize: (storedState: string) => {
          try {
            if (!storedState) {
              return {};
            }
            const bytes = CryptoJS.AES.decrypt(storedState, secretKey);
            const decryptedState = bytes.toString(CryptoJS.enc.Utf8);
            if (!decryptedState) {
              console.warn(
                "Could not decrypt state, possibly due to a key change or corrupted data. Resetting state."
              );
              return {};
            }
            const parsed = JSON.parse(decryptedState) as {
              user?: User | null;
              currentServer?: Partial<OwnedSelectedServer> | null;
            };
            return {
              user: parsed.user,
              currentServer: parsed.currentServer
                ? ({
                  ...parsed.currentServer,
                  isPremium: null,
                  tierFetchStatus: "idle" as const,
                } as OwnedSelectedServer)
                : null,
            };
          } catch (e) {
            console.error("Could not deserialize and decrypt state:", e);
            return {};
          }
        },
      },
    } as any,
  }
);